import { useMemo, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { resolveApiError, useTranslation } from '@eon/i18n';
import {
  Badge,
  Button,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  PageHeader,
  Pagination,
  toast,
  type Column,
} from '@eon/ui';
import { hooks } from '@eon/api-client';
import type { Device } from '@eon/types';
import { cn, formatRelative } from '@eon/utils';
import {
  CheckCircle2,
  Eye,
  KeyRound,
  MoreHorizontal,
  Pencil,
  PlugZap,
  RotateCcw,
  Rss,
  Search,
  Terminal,
  Plus,
  Trash2,
  UserPlus,
  Wifi,
  WifiOff,
  Zap,
} from 'lucide-react';
import { usePaginationLabels } from '@/lib/use-pagination-labels';
import { useTableSort } from '@/lib/table-sort';
import { DeviceStatusBadge } from '@/components/status/StatusBadges';
import { connectorStatusVariant, translateConnectorStatus } from '@eon/i18n';
function useDeviceFilters() {
  const { t } = useTranslation();
  const STATUS_FILTERS: { id: string; label: string }[] = [
    { id: '', label: t('device.all') },
    { id: 'online', label: t('device.status.available') },
    { id: 'offline', label: t('device.status.offline') },
    { id: 'available', label: t('device.status.available') },
    { id: 'charging', label: t('device.status.charging') },
    { id: 'faulted', label: t('device.status.faulted') },
  ];

  const REGISTRATION_FILTERS: { id: '' | 'true' | 'false'; label: string }[] = [
    { id: '', label: t('device.all') },
    { id: 'true', label: t('device.registered') },
    { id: 'false', label: t('device.pending') },
  ];

  return { STATUS_FILTERS, REGISTRATION_FILTERS };
}

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

function ConnectorChips({ device }: { device: Device }) {
  const { t } = useTranslation();
  const counts = useMemo(() => {
    const acc: Record<string, number> = {};
    for (const c of device.connectors ?? []) {
      const key = c.status || 'Unknown';
      acc[key] = (acc[key] ?? 0) + 1;
    }
    return acc;
  }, [device.connectors]);

  const total = device.connectors?.length ?? 0;
  if (total === 0) {
    return <span className="text-muted-foreground text-xs">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {Object.entries(counts).map(([status, n]) => (
        <Badge key={status} variant={connectorStatusVariant(status)}>
          {t('device.connectorCount', { count: n, status: translateConnectorStatus(t, status) })}
        </Badge>
      ))}
    </div>
  );
}

function ConnectionCell({ device }: { device: Device }) {
  const { t } = useTranslation();
  const isOffline = !hooks.isDeviceOnline(device);
  return (
    <div
      className="flex items-center justify-center"
      title={isOffline ? t('common.offline') : t('common.online')}
    >
      <div
        className={cn(
          'flex h-8 w-8 items-center justify-center rounded-full',
          isOffline ? 'bg-destructive/10 text-destructive' : 'bg-emerald-500/10 text-emerald-500',
        )}
      >
        {isOffline ? <WifiOff className="h-4 w-4" /> : <Wifi className="h-4 w-4" />}
      </div>
    </div>
  );
}

export function DevicesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const labels = usePaginationLabels();
  const { STATUS_FILTERS, REGISTRATION_FILTERS } = useDeviceFilters();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<string>('');
  const [registered, setRegistered] = useState<'' | 'true' | 'false'>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const { sort, onSortChange, sortParams } = useTableSort(() => setPage(1));
  const list = hooks.useAdminDevices({
    q,
    status: status || undefined,
    registered: registered || undefined,
    page,
    pageSize,
    ...sortParams,
  });
  const counts = hooks.useAdminDeviceCounts();
  const del = hooks.useDeleteDevice();
  const register = hooks.useRegisterDevice();

  const totalCount = list.data?.totalCount ?? 0;
  const totalPages = list.data?.totalPages ?? 1;

  const onDelete = (d: Device) => {
    if (!window.confirm(`"${d.displayName ?? d.serialNumber}"`)) return;
    del.mutate(d.id, {
      onSuccess: () => toast.success(t('actions.delete')),
      onError: (e) => toast.error(resolveApiError(e, t)),
    });
  };

  const onRegister = (d: Device) => {
    register.mutate(
      { id: d.id },
      {
        onSuccess: () => toast.success(t('actions.approve')),
        onError: (e) => toast.error(resolveApiError(e, t)),
      },
    );
  };

  const columns: Column<Device>[] = [
    {
      key: 'connection',
      header: '',
      className: 'w-12',
      cell: (d) => <ConnectionCell device={d} />,
    },
    {
      key: 'identity',
      header: t('nav.devices'),
      sortable: true,
      sortKey: 'identity',
      sortValue: (d) => d.displayName?.trim() || d.serialNumber,
      cell: (d) => (
        <Link
          to="/devices/$deviceId"
          params={{ deviceId: d.id }}
          className="flex flex-col hover:underline"
        >
          <span className="font-medium">{d.displayName?.trim() || d.serialNumber}</span>
          <span className="text-muted-foreground font-mono text-xs">{d.serialNumber}</span>
        </Link>
      ),
    },
    {
      key: 'vendor',
      header: t('device.model'),
      sortable: true,
      sortValue: (d) => d.vendor ?? d.model,
      cell: (d) =>
        d.vendor || d.model ? (
          <div className="flex flex-col">
            <span>{d.vendor ?? '—'}</span>
            <span className="text-muted-foreground text-xs">{d.model ?? ''}</span>
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'status',
      header: t('common.status'),
      sortable: true,
      cell: (d) => <DeviceStatusBadge status={d.status} />,
    },
    {
      key: 'connectors',
      header: t('device.connectors'),
      cell: (d) => <ConnectorChips device={d} />,
    },
    {
      key: 'fw',
      header: t('device.firmware'),
      cell: (d) => (
        <span className="text-muted-foreground font-mono text-xs">
          {d.firmwareVersion ?? d.firmware ?? '—'}
        </span>
      ),
    },
    {
      key: 'seen',
      header: t('device.lastSeen'),
      sortable: true,
      sortKey: 'seen',
      sortValue: (d) => d.lastHeartbeatAt ?? d.lastHeartbeat,
      cell: (d) => (
        <span className="text-muted-foreground">
          {formatRelative(d.lastHeartbeatAt ?? d.lastHeartbeat)}
        </span>
      ),
    },
    {
      key: 'registered',
      header: t('device.registrationStatus'),
      sortable: true,
      sortValue: (d) => d.registered,
      cell: (d) =>
        d.registered ? (
          <Badge variant="success" className="gap-1">
            <CheckCircle2 className="h-3 w-3" /> {t('common.yes')}
          </Badge>
        ) : (
          <Button
            size="sm"
            variant="outline"
            disabled={register.isPending}
            onClick={() => onRegister(d)}
            className="h-7 gap-1"
          >
            <UserPlus className="h-3.5 w-3.5" /> {t('actions.approve')}
          </Button>
        ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-16 text-right',
      cell: (d) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Aksiyonlar">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem onSelect={() => navigate({ to: '/devices/$deviceId', params: { deviceId: d.id } })}>
              <Eye className="mr-2 h-4 w-4" /> {t('actions.more')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => navigate({ to: '/devices/$deviceId/control', params: { deviceId: d.id } })}
            >
              <Terminal className="mr-2 h-4 w-4" /> Control
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => navigate({ to: '/devices/$deviceId/console', params: { deviceId: d.id } })}
            >
              <Rss className="mr-2 h-4 w-4" /> Console
            </DropdownMenuItem>
            {!d.registered ? (
              <DropdownMenuItem onSelect={() => onRegister(d)}>
                <UserPlus className="mr-2 h-4 w-4" /> {t('actions.approve')}
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem
              onSelect={() => navigate({ to: '/devices/$deviceId', params: { deviceId: d.id }, hash: 'edit' })}
            >
              <Pencil className="mr-2 h-4 w-4" /> {t('actions.edit')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() =>
                navigate({ to: '/devices/$deviceId', params: { deviceId: d.id }, hash: 'local-auth' })
              }
            >
              <KeyRound className="mr-2 h-4 w-4" /> Local Auth List
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => onDelete(d)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" /> {t('actions.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const description = counts.data
    ? `${counts.data.total.toLocaleString()} ${t('nav.devices')} · ${counts.data.online} ${t('dashboard.onlineDevices')} · ${counts.data.offline} ${t('dashboard.offline')}${
        counts.data.faulted > 0 ? ` · ${counts.data.faulted} ${t('dashboard.faulted')}` : ''
      }${counts.data.unregistered > 0 ? ` · ${counts.data.unregistered} ${t('device.pending')}` : ''}`
    : list.data
      ? `${totalCount.toLocaleString()} ${t('nav.devices')}`
      : undefined;

  const showPendingBanner = counts.data && counts.data.unregistered > 0 && registered !== 'false';

  return (
    <>
      <PageHeader
        title={t('nav.devices')}
        description={description}
        actions={
          <>
            <Button asChild>
              <Link to="/devices/new">
                <Plus className="mr-2 h-4 w-4" /> {t('device.create.new')}
              </Link>
            </Button>
            <div className="relative w-full sm:w-auto">
              <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
              <Input
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setPage(1);
                }}
                placeholder={t('actions.search')}
                className="w-full pl-9 sm:w-64"
              />
            </div>
            <Button variant="outline" onClick={() => list.refetch()}>
              <RotateCcw className="mr-2 h-4 w-4" /> {t('actions.refresh')}
            </Button>
          </>
        }
      />

      {showPendingBanner ? (
        <div className="bg-amber-400/10 text-amber-800 border-amber-400/40 dark:text-amber-200 mb-3 flex items-center gap-3 rounded-md border px-3 py-2 text-sm">
          <UserPlus className="h-4 w-4" />
          <span>
            {t('device.pendingBanner', { count: counts.data!.unregistered })}
          </span>
          <Button
            size="sm"
            variant="outline"
            className="ml-auto"
            onClick={() => {
              setRegistered('false');
              setStatus('');
              setPage(1);
            }}
          >
            {t('actions.showPending')}
          </Button>
        </div>
      ) : null}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="text-muted-foreground text-xs uppercase tracking-wider">{t('device.registrationStatus')}</span>
        {REGISTRATION_FILTERS.map((f) => (
          <Button
            key={f.id || 'all'}
            variant={registered === f.id ? 'default' : 'outline'}
            size="sm"
            onClick={() => {
              setRegistered(f.id);
              setPage(1);
            }}
          >
            {f.label}
            {f.id === 'false' && counts.data?.unregistered ? (
              <span
                className={cn(
                  'ml-2 rounded px-1.5 py-0.5 text-[10px] font-medium tabular-nums',
                  registered === 'false' ? 'bg-primary-foreground/20' : 'bg-amber-400/30',
                )}
              >
                {counts.data.unregistered}
              </span>
            ) : null}
          </Button>
        ))}
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="text-muted-foreground text-xs uppercase tracking-wider">{t('common.status')}</span>
        {STATUS_FILTERS.map((f) => {
          const active = status === f.id;
          const n = counts.data
            ? f.id === ''
              ? counts.data.total
              : (counts.data[f.id as keyof typeof counts.data] as number | undefined)
            : undefined;
          return (
            <Button
              key={f.id || 'all'}
              variant={active ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                setStatus(f.id);
                setPage(1);
              }}
            >
              {f.id === 'online' ? <Wifi className="mr-1.5 h-3.5 w-3.5" /> : null}
              {f.id === 'offline' ? <WifiOff className="mr-1.5 h-3.5 w-3.5" /> : null}
              {f.id === 'charging' ? <Zap className="mr-1.5 h-3.5 w-3.5" /> : null}
              {f.id === 'faulted' ? <PlugZap className="mr-1.5 h-3.5 w-3.5" /> : null}
              {f.label}
              {typeof n === 'number' ? (
                <span
                  className={cn(
                    'ml-2 rounded px-1.5 py-0.5 text-[10px] font-medium tabular-nums',
                    active ? 'bg-primary-foreground/20' : 'bg-muted',
                  )}
                >
                  {n}
                </span>
              ) : null}
            </Button>
          );
        })}
      </div>

      <DataTable
        data={list.data?.data}
        columns={columns}
        loading={list.isLoading}
        rowKey={(d) => d.id}
        emptyTitle={t('device.notFound')}
        emptyDescription={t('device.changeFilter')}
        sort={sort}
        onSortChange={onSortChange}
      />

      {list.data && totalCount > 0 ? (
        <Pagination
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          loading={list.isFetching}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          labels={labels}
        />
      ) : null}
    </>
  );
}
